import { Company, IndicativePayoutRate, Invoice, InvoiceItem } from "@/types";
import {
  Currency,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
} from "@/money/currency_constants";
import {
  entries,
  filter,
  groupBy,
  keyBy,
  map,
  mapValues,
  sortBy,
  sumBy,
} from "lodash-es";
import { calculateTotalAmountFromInvoiceItemsWithVAT } from "./moneyUtils";

export type Payout = {
  company_id: number;
  totalAmount: number;
  items: InvoiceItem[];
};

export const getPendingPayouts = (invoiceItems: InvoiceItem[]) => {
  if (!invoiceItems?.length) {
    return [];
  }

  const invoicesByCompanyId = groupBy(invoiceItems, "company_id");

  const payouts = map(
    entries(invoicesByCompanyId),
    ([_company_id, items]): Payout => {
      const company_id = parseInt(_company_id);

      if (isNaN(company_id)) {
        throw new Error(`Invalid company_id: ${_company_id}`);
      }

      return {
        company_id,
        totalAmount: calculateTotalAmountFromInvoiceItemsWithVAT(items),
        items,
      };
    }
  );

  return payouts;
};

/** Sums display_amount per currency: invoices issued in different currencies are never added together. */
export const sumDisplayAmountByCurrency = (
  invoices: Invoice[]
): Record<string, number> =>
  mapValues(groupBy(invoices, "currency"), (group) =>
    sumBy(group, "display_amount")
  );

// Wider than any display_amount, so the currency always dominates the amount in the sort key.
const CURRENCY_SORT_SPAN = 1e12;

/** A numeric sort key ordering invoices by currency (in SUPPORTED_CURRENCIES order), then by amount within it. */
export const currencyGroupedAmount = (
  invoice: Pick<Invoice, "currency" | "display_amount">
): number =>
  SUPPORTED_CURRENCIES.indexOf(invoice.currency as Currency) *
    CURRENCY_SORT_SPAN +
  invoice.display_amount;

/**
 * A pending payout at today's ECB rate in the company's payout currency, for a "Money pending" figure.
 *
 * @property rate - Units of `currency` per 1 EUR, as the ECB publishes it
 * @property rateDate - The ECB publication date of `rate`
 */
export interface IndicativePayout {
  amount: number;
  currency: Currency;
  rate: string;
  rateDate: string;
}

/**
 * The currency a company's pending payout is shown in: its payout currency while today's ECB rate
 * is known, else EUR, the currency the amount is owed in.
 */
export const displayedPayoutCurrency = (
  payoutCurrency: Currency | undefined,
  rate: IndicativePayoutRate | null | undefined
): Currency => (rate && payoutCurrency ? payoutCurrency : DEFAULT_CURRENCY);

/**
 * An EUR amount owed to the company, approximated in its payout currency at today's ECB rate.
 * An indication only: the payout itself converts each line at its own date's rate. Null for an
 * EUR company, when no recent rate is known, or when nothing is owed to the company.
 */
export const indicativePayout = (
  eurAmount: number,
  payoutCurrency: Currency | undefined,
  rate: IndicativePayoutRate | null | undefined
): IndicativePayout | null => {
  const currency = displayedPayoutCurrency(payoutCurrency, rate);
  return eurAmount > 0 && rate && currency !== DEFAULT_CURRENCY
    ? {
        amount: eurAmount * Number(rate.rate),
        currency,
        rate: rate.rate,
        rateDate: rate.rate_date,
      }
    : null;
};

/**
 * The EUR amount owed in one payout currency, with that currency's rate for the "Money pending"
 * figure. EUR carries no rate.
 */
export interface PendingPayoutPart {
  eurAmount: number;
  payoutCurrency: Currency;
  indicativePayoutRate: IndicativePayoutRate | null;
}

/**
 * Pending payouts summed per payout currency, in SUPPORTED_CURRENCIES order, so companies paid out
 * in different currencies are never added together. A company whose payout currency has no recent
 * rate counts in EUR, as its own figure shows it. Companies net within a currency; a currency
 * nothing is owed in is left out, so no pending payout gives an empty list.
 */
export const pendingPayoutsByPayoutCurrency = (
  invoiceItems: InvoiceItem[],
  companies: Company[]
): PendingPayoutPart[] => {
  const companiesById = keyBy(companies, "id");
  const companyParts = map(
    getPendingPayouts(invoiceItems),
    (payout): PendingPayoutPart => {
      // Undefined while a dashboard still holds the previous company's items, until its refetch
      // lands; those count in EUR, the currency they are owed in.
      const company: Company | undefined = companiesById[payout.company_id];
      const payoutCurrency = displayedPayoutCurrency(
        company?.payout_currency,
        company?.indicative_payout_rate
      );
      return {
        eurAmount: payout.totalAmount,
        payoutCurrency,
        indicativePayoutRate:
          payoutCurrency === DEFAULT_CURRENCY
            ? null
            : (company?.indicative_payout_rate ?? null),
      };
    }
  );
  const currencyParts = map(
    groupBy(companyParts, "payoutCurrency"),
    (group): PendingPayoutPart => ({
      ...group[0],
      eurAmount: sumBy(group, "eurAmount"),
    })
  );
  return sortBy(
    filter(currencyParts, (part) => part.eurAmount > 0),
    (part) => SUPPORTED_CURRENCIES.indexOf(part.payoutCurrency)
  );
};
